if (!window.VideoGalleryPro) {
  class VideoGalleryPro {
    constructor(section) {
      this.section = section;
      this.gallery = section.querySelector('[data-video-gallery]');
      if (!this.gallery) return;

      this.layout = this.gallery.dataset.layout || 'stack';
      if (this.layout !== 'slider') return;

      this.nav = section.querySelector('[data-video-gallery-nav]');
      this.slides = Array.from(this.gallery.querySelectorAll('.video-gallery__slide'));
      if (!this.slides.length) return;

      this.prevBtn = this.nav ? this.nav.querySelector('[data-action="prev"]') : null;
      this.nextBtn = this.nav ? this.nav.querySelector('[data-action="next"]') : null;
      this.dots = this.nav ? Array.from(this.nav.querySelectorAll('.video-gallery__dot')) : [];
      this.currentEl = this.nav ? this.nav.querySelector('[data-current]') : null;

      this.autoplayEnabled = this.gallery.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.gallery.dataset.autoplaySpeed || '5000', 10);
      this.pauseOnHover = this.gallery.dataset.pauseOnHover === 'true';
      this.stopOtherVideos = this.gallery.dataset.stopOtherVideos === 'true';
      this.dragThresholdRatio = Math.max(0.05, Math.min(0.5, parseFloat(this.gallery.dataset.dragThreshold || '0.18')));
      this.flickVelocity = Math.max(0.1, Math.min(2, parseFloat(this.gallery.dataset.flickVelocity || '0.45')));

      this.currentIndex = 0;
      this.autoTimer = null;
      this.scrollTimer = null;
      this.isHovering = false;

      this.isPointerDown = false;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.startScrollLeft = 0;
      this.lastX = 0;
      this.lastTime = 0;
      this.velocityX = 0;

      this.boundOnPointerMove = this.onPointerMove.bind(this);
      this.boundOnPointerUp = this.onPointerUp.bind(this);
      this.boundOnVisibilityChange = this.onVisibilityChange.bind(this);
      this.boundOnResize = this.onResize.bind(this);
      this.boundOnGalleryScroll = this.onGalleryScroll.bind(this);
      this.boundOnVideoPlay = this.onVideoPlay.bind(this);

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateUI();
      this.goTo(0, 'auto');
      this.startAutoplay();
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.goTo(this.currentIndex - 1);
          this.startAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.goTo(this.currentIndex + 1);
          this.startAutoplay();
        });
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.goTo(index);
          this.startAutoplay();
        });
      });

      this.gallery.addEventListener('scroll', this.boundOnGalleryScroll, { passive: true });

      if (this.pauseOnHover) {
        this.gallery.addEventListener('mouseenter', () => {
          this.isHovering = true;
        });

        this.gallery.addEventListener('mouseleave', () => {
          this.isHovering = false;
        });
      }

      this.gallery.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      window.addEventListener('pointermove', this.boundOnPointerMove, { passive: false });
      window.addEventListener('pointerup', this.boundOnPointerUp);
      window.addEventListener('pointercancel', this.boundOnPointerUp);

      this.gallery.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });

      this.gallery.addEventListener('theme:video:play', this.boundOnVideoPlay);
      document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
      window.addEventListener('resize', this.boundOnResize);
    }

    destroy() {
      this.stopAutoplay();

      this.gallery.removeEventListener('scroll', this.boundOnGalleryScroll);
      this.gallery.removeEventListener('theme:video:play', this.boundOnVideoPlay);
      document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
      window.removeEventListener('resize', this.boundOnResize);
      window.removeEventListener('pointermove', this.boundOnPointerMove);
      window.removeEventListener('pointerup', this.boundOnPointerUp);
      window.removeEventListener('pointercancel', this.boundOnPointerUp);
    }

    clamp(num, min, max) {
      return Math.max(min, Math.min(max, num));
    }

    getScrollLeftForIndex(index) {
      const slide = this.slides[index];
      return slide ? slide.offsetLeft : 0;
    }

    requestPauseSlideMedia(slide) {
      if (!slide) return;

      slide.querySelectorAll('[data-video-wrapper]').forEach((videoRoot) => {
        videoRoot.dispatchEvent(new CustomEvent('theme:video:pause-request', {
          bubbles: true
        }));
      });
    }

    pauseOtherSlidesMedia(activeIndex) {
      if (!this.stopOtherVideos) return;

      this.slides.forEach((slide, index) => {
        if (index !== activeIndex) {
          this.requestPauseSlideMedia(slide);
        }
      });
    }

    updateUI() {
      if (this.currentEl) this.currentEl.textContent = this.currentIndex + 1;

      this.dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === this.currentIndex);
      });

      if (this.prevBtn) this.prevBtn.disabled = this.currentIndex <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentIndex >= this.slides.length - 1;

      this.pauseOtherSlidesMedia(this.currentIndex);
    }

    goTo(index, behavior = 'smooth') {
      index = this.clamp(index, 0, this.slides.length - 1);
      this.currentIndex = index;

      this.gallery.scrollTo({
        left: this.getScrollLeftForIndex(index),
        behavior
      });

      this.updateUI();
    }

    detectCurrentIndex() {
      let closestIndex = 0;
      let closestDistance = Infinity;
      const galleryLeft = this.gallery.getBoundingClientRect().left;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - galleryLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.currentIndex) {
        this.currentIndex = closestIndex;
        this.updateUI();
      }
    }

    stopAutoplay() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.slides.length <= 1) return;

      this.stopAutoplay();

      this.autoTimer = setInterval(() => {
        if (document.hidden) return;
        if (this.pauseOnHover && this.isHovering) return;
        if (this.isPointerDown || this.isDragging) return;

        let nextIndex = this.currentIndex + 1;
        if (nextIndex > this.slides.length - 1) nextIndex = 0;
        this.goTo(nextIndex);
      }, this.autoplaySpeed);
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.isPointerDown = true;
      this.isDragging = false;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.lastX = event.clientX;
      this.startScrollLeft = this.gallery.scrollLeft;
      this.lastTime = Date.now();
      this.velocityX = 0;

      this.gallery.classList.add('is-dragging');
      this.stopAutoplay();
    }

    onPointerMove(event) {
      if (!this.isPointerDown) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      if (!this.isDragging) {
        if (Math.abs(deltaX) < 4 && Math.abs(deltaY) < 4) return;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.isPointerDown = false;
          this.gallery.classList.remove('is-dragging');
          this.startAutoplay();
          return;
        }

        this.isDragging = true;
      }

      event.preventDefault();

      const now = Date.now();
      const moveDeltaX = event.clientX - this.lastX;
      const moveDeltaTime = Math.max(1, now - this.lastTime);

      this.velocityX = moveDeltaX / moveDeltaTime;
      this.gallery.scrollLeft = this.startScrollLeft - deltaX;

      this.lastX = event.clientX;
      this.lastTime = now;
    }

    onPointerUp() {
      if (!this.isPointerDown && !this.isDragging) return;

      const deltaX = this.lastX - this.startX;
      const dragDistance = Math.abs(deltaX);
      const pageWidth = Math.max(1, this.slides[0].offsetWidth);
      const currentScroll = this.gallery.scrollLeft;
      const nearestIndex = this.clamp(Math.round(currentScroll / pageWidth), 0, this.slides.length - 1);

      this.gallery.classList.remove('is-dragging');

      if (!this.isDragging) {
        this.isPointerDown = false;
        this.startAutoplay();
        return;
      }

      const absVelocity = Math.abs(this.velocityX);
      const passedDistanceThreshold = dragDistance > pageWidth * this.dragThresholdRatio;
      const passedVelocityThreshold = absVelocity > this.flickVelocity;

      let targetIndex = nearestIndex;

      if (passedVelocityThreshold || passedDistanceThreshold) {
        if (deltaX < 0) {
          targetIndex = this.clamp(this.currentIndex + 1, 0, this.slides.length - 1);
        } else if (deltaX > 0) {
          targetIndex = this.clamp(this.currentIndex - 1, 0, this.slides.length - 1);
        }
      }

      this.isPointerDown = false;
      this.isDragging = false;

      this.goTo(targetIndex);
      this.startAutoplay();
    }

    onGalleryScroll() {
      if (this.scrollTimer) clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        if (!this.isDragging) this.detectCurrentIndex();
      }, 60);
    }

    onVideoPlay(event) {
      const activeWrapper = event.target.closest('.video-gallery__slide');
      if (!activeWrapper) return;

      const activeIndex = parseInt(activeWrapper.dataset.index || '0', 10);
      this.currentIndex = activeIndex;
      this.updateUI();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    onResize() {
      this.goTo(this.currentIndex, 'auto');
    }
  }

  window.VideoGalleryPro = VideoGalleryPro;
}

if (!window.__videoGalleryProInit) {
  window.__videoGalleryProInit = true;
  window.__videoGalleryProInstances = window.__videoGalleryProInstances || new Map();

  window.initVideoGalleryPro = function(scope = document) {
    const sections = scope.querySelectorAll('[data-video-gallery-section]');
    sections.forEach((section) => {
      const sectionId = section.dataset.sectionId || Math.random().toString(36).slice(2);

      if (window.__videoGalleryProInstances.has(sectionId)) return;

      const instance = new window.VideoGalleryPro(section);
      window.__videoGalleryProInstances.set(sectionId, instance);
    });
  };

  window.destroyVideoGalleryPro = function(sectionId) {
    const instance = window.__videoGalleryProInstances.get(sectionId);
    if (!instance) return;

    instance.destroy();
    window.__videoGalleryProInstances.delete(sectionId);
  };

  document.addEventListener('DOMContentLoaded', () => {
    window.initVideoGalleryPro(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    window.initVideoGalleryPro(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-video-gallery-section]');
    if (!section) return;

    const sectionId = section.dataset.sectionId;
    if (!sectionId) return;

    window.destroyVideoGalleryPro(sectionId);
  });
}